import { useAuth } from '@/store/authStore';
import { Permission, Role, ROLE_PERMISSIONS } from '@/types/permissions';

/**
 * 权限控制Hook
 * 提供权限检查功能
 */
export const usePermissions = () => {
  const { user } = useAuth();

  /**
   * 检查用户是否拥有指定权限
   */
  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    
    // 如果是admin角色，拥有所有权限
    if (user.role === Role.ADMIN) return true;

    const permissionList = Array.isArray(user.permissions) ? user.permissions : [];
    if ((permissionList as unknown as string[]).includes('*')) return true;
    
    // 合并角色默认权限和用户自定义权限
    const rolePerms = ROLE_PERMISSIONS[user.role] || [];
    return rolePerms.includes(permission) || permissionList.includes(permission);
  };

  /**
   * 检查用户是否拥有任意一个权限
   */
  const hasAnyPermission = (permissions: Permission[]): boolean => {
    if (!user) return false;
    return permissions.some(permission => hasPermission(permission));
  };

  /**
   * 检查用户是否拥有所有权限
   */
  const hasAllPermissions = (permissions: Permission[]): boolean => {
    if (!user) return false;
    return permissions.every(permission => hasPermission(permission));
  };

  /**
   * 检查用户角色
   */
  const hasRole = (role: Role): boolean => {
    if (!user) return false;
    return user.role === role;
  };

  /**
   * 检查用户是否拥有指定角色中的任意一个
   */
  const hasAnyRole = (roles: Role[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  /**
   * 检查用户角色是否至少是指定级别
   * 角色级别: trainee < staff < supervisor < manager < admin
   */
  const hasMinimumRole = (minimumRole: Role): boolean => {
    if (!user) return false;
    
    const roleHierarchy: Record<Role, number> = {
      [Role.TRAINEE]: 1,
      [Role.STAFF]: 2,
      [Role.SUPERVISOR]: 3,
      [Role.MANAGER]: 4,
      [Role.ADMIN]: 5,
    };
    
    const userRoleLevel = roleHierarchy[user.role] || 0;
    const minimumRoleLevel = roleHierarchy[minimumRole] || 0;
    
    return userRoleLevel >= minimumRoleLevel;
  };

  /**
   * 获取用户有权限访问的权限列表
   */
  const getAvailablePermissions = (): Permission[] => {
    if (!user) return [];
    
    // 如果是admin，返回所有权限
    if (user.role === Role.ADMIN) {
      return Object.values(Permission) as Permission[];
    }

    if ((user.permissions as unknown as string[] | undefined)?.includes('*')) {
      return Object.values(Permission) as Permission[];
    }
    
    // 合并角色默认权限和用户自定义权限
    const rolePerms = ROLE_PERMISSIONS[user.role] || [];
    const customPerms = user.permissions || [];
    return [...new Set([...rolePerms, ...customPerms])];
  };

  /**
   * 获取用户角色对应的默认权限
   */
  const getRoleDefaultPermissions = (): Permission[] => {
    if (!user) return [];
    return ROLE_PERMISSIONS[user.role] || [];
  };

  /**
   * 检查用户是否具有管理员权限（admin或manager）
   */
  const isAdmin = (): boolean => {
    return hasAnyRole([Role.ADMIN, Role.MANAGER]);
  };

  /**
   * 检查用户是否可以管理员工
   */
  const canManageStaff = (): boolean => {
    return hasPermission(Permission.MANAGE_STAFF);
  };

  /**
   * 检查用户是否可以查看财务数据
   */
  const canViewFinancials = (): boolean => {
    return hasAnyPermission([
      Permission.VIEW_REVENUE,
      Permission.VIEW_FINANCIAL_REPORTS
    ]);
  };

  return {
    // 基础权限检查
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    
    // 角色检查
    hasRole,
    hasAnyRole,
    hasMinimumRole,
    
    // 权限获取
    getAvailablePermissions,
    getRoleDefaultPermissions,
    
    // 便捷方法
    isAdmin,
    canManageStaff,
    canViewFinancials,
    
    // 用户信息
    user,
    userRole: user?.role ?? null,
    userPermissions: user?.permissions || [],
  };
};
